import {
  Button,
  SearchableSelect,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  IconTextItem,
  Input,
} from '@/shared/components';
import { type EditCryptoAddressFormValues, type NetworkOption } from '../model';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { getRequiredLabel } from '@/shared/lib';
import { CryptoAddressField } from '@/entities/crypto-address';

type Props = {
  form: UseFormReturn<EditCryptoAddressFormValues>;
  networkOptions: NetworkOption[];
  updateAddress: () => Promise<void>;
  deleteAddress: () => Promise<void>;
  isPending: boolean;
};

export const EditCryptoAddressForm = ({
  form,
  networkOptions,
  updateAddress,
  isPending,
  deleteAddress,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-[1.6rem]"
        onSubmit={form.handleSubmit(updateAddress)}>
        <FormField
          control={form.control}
          name="networkName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SearchableSelect<NetworkOption>
                  {...field}
                  disabled
                  floatingLabel={getRequiredLabel(
                    t('savedAddresses.cryptoAddress.fields.networkLabel')
                  )}
                  items={networkOptions}
                  renderItem={network => (
                    <div className="flex items-center justify-between w-full">
                      <IconTextItem title={network.code} name={network.name} />
                    </div>
                  )}
                  renderSelectedValue={value => {
                    const network = networkOptions.find(
                      network => network.value === value
                    );

                    if (!network) return <></>;

                    return (
                      <div className="flex items-center justify-between w-full">
                        <IconTextItem
                          title={network.name + ' ' + network.code}
                          variant="compact"
                        />
                      </div>
                    );
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cryptoAddress"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <CryptoAddressField
                  {...field}
                  label={getRequiredLabel(
                    t('savedAddresses.cryptoAddress.fields.cryptoAddressLabel')
                  )}
                  onChange={field.onChange}
                  disabled
                />
              </FormControl>
              <FormMessage className="px-[1.6rem]" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="templateName"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={getRequiredLabel(
                      t('savedAddresses.cryptoAddress.fields.templateNameLabel')
                    )}
                    className="p3"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="px-[1.6rem]" />
              </FormItem>
            );
          }}
        />

        <Button
          size="lg"
          type="submit"
          loading={isPending}
          className="mt-[1.6rem]">
          {t('savedAddresses.cryptoAddress.editing.buttonText')}
        </Button>

        <Button
          size="lg"
          type="button"
          variant="secondary"
          onClick={deleteAddress}
          disabled={isPending}>
          {t('savedAddresses.cryptoAddress.deletion.buttonText')}
        </Button>
      </form>
    </Form>
  );
};
